import React, { ReactNode, useCallback, useRef, useState } from "react";
import classNames from "./ResizePanelVertical.module.css";
import { useMeeting } from "../../../hooks/meeting";

export type ResizePanelVerticalProps = {
  children: ReactNode[];
  minTopHeightInPixels: number;
  minBottomHeightInPixels: number;
};

const ResizePanelVertical = (props: ResizePanelVerticalProps) => {
  const resizePanel = useRef<HTMLDivElement>(null);
  const [meeting, setMeeting] = useMeeting();

  const setTopHeight = useCallback(
    (newParticipantsGridHeightInPercent: number) => {
      setMeeting((prevState) => ({
        ...prevState,
        layout: {
          ...prevState.layout,
          participantsGridHeightInPercent: newParticipantsGridHeightInPercent,
        },
      }));
    },
    [setMeeting]
  );

  const [resizing, setResizing] = useState(false);

  const resize = useCallback(
    (event: MouseEvent) => {
      const totalHeightInPixels = resizePanel.current?.clientHeight;
      const offset = -20;
      if (totalHeightInPixels) {
        // Make sure we are in minimum heights
        const height = event.clientY - offset;
        if (
          height > props.minTopHeightInPixels &&
          totalHeightInPixels - height > props.minBottomHeightInPixels
        ) {
          const newTopHeight = (height / totalHeightInPixels) * 100;
          setTopHeight(newTopHeight);
        }
      }
    },
    [props.minTopHeightInPixels, props.minBottomHeightInPixels, setTopHeight]
  );

  const endResize = useCallback(() => {
    setResizing(false);
    document.body.style.cursor = "auto";
    window.removeEventListener("mousemove", resize);
  }, [resize]);

  const startResize = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setResizing(true);
      document.body.style.cursor = "grabbing";
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", endResize);
    },
    [endResize, resize]
  );

  const resizeTouch = useCallback(
    (event: TouchEvent) => {
      const totalHeightInPixels = resizePanel.current?.clientHeight;
      const offset = -20;
      if (totalHeightInPixels) {
        // Make sure we are in minimum heights
        const height = event.touches[0].clientY - offset;
        if (
          height > props.minTopHeightInPixels &&
          totalHeightInPixels - height > props.minBottomHeightInPixels
        ) {
          const newTopHeight = (height / totalHeightInPixels) * 100;
          setTopHeight(newTopHeight);
        }
      }
    },
    [props.minTopHeightInPixels, props.minBottomHeightInPixels, setTopHeight]
  );

  const endResizeTouch = useCallback(() => {
    setResizing(false);
    document.body.style.cursor = "auto";
    window.removeEventListener("touchmove", resizeTouch);
  }, [resizeTouch]);

  const startResizeTouch = useCallback(
    (event: React.TouchEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setResizing(true);
      document.body.style.cursor = "grabbing";
      window.addEventListener("touchmove", resizeTouch);
      window.addEventListener("touchend", endResizeTouch);
    },
    [resizeTouch, endResizeTouch]
  );

  const children = props.children.filter((child) => !!child);

  if (!children.length) {
    return null;
  } else if (children.length < 2) {
    return (
      <div ref={resizePanel} className={classNames.ResizePanelVertical}>
        {children[0]}
      </div>
    );
  } else {
    return (
      <div ref={resizePanel} className={classNames.ResizePanelVertical}>
        <div
          style={{
            height: `max(${meeting.layout.participantsGridHeightInPercent}%, ${props.minTopHeightInPixels}px)`,
            display: "flex",
          }}
        >
          {children[0]}
        </div>
        <div
          className={`${classNames.ResizeGutter} ${
            resizing ? classNames.Resizing : ""
          }`}
          onMouseDown={startResize}
          onTouchStart={startResizeTouch}
        >
          <div />
        </div>
        <div
          style={{
            height: `${100 - meeting.layout.participantsGridHeightInPercent}%`,
            display: "flex",
          }}
        >
          {children[1]}
        </div>
      </div>
    );
  }
};

export default ResizePanelVertical;
